import {
    CloseOutlined,
    FilterAltOffOutlined,
    FilterAltOutlined,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    Grid,
    IconButton,
    TextField,
    Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { GET } from "../../../Utilities/BaseService";
import { MultiPermissionAuthorize } from "../../../Utilities/MultiPermissionAuthorize";
import UpdatedSearchableInput from "../../../Utilities/UpdatedSearchableInput";
import BaseTable from "../../Table/BaseTable";
import SearchBar from "../../Table/Components/SearchBar";
import { currencyFormatter } from "../Bill/Cells/Last12Summary";
const initialClearAll = {
    search: false,
    district: false,
    powerstation: false,
    village: false,
    hamlet: false,
};

const CreditPaymentLeasure = () => {
    const [filter, setFilter] = useState<any>({
        search: "",
        meterNo: "",
        district: "",
        powerstation: "",
        village: "",
        hamlet: "",
    });
    const [clearAll, setClearAll] = useState(initialClearAll);

    const handleClearAll = () => {
        // reset UI filters

        // reset applied filters
        setFilter({ ...filter, search: "" });

        // notify searchable inputs
        setClearAll({
            search: true,
            district: true,
            powerstation: true,
            village: true,
            hamlet: true,
        });

        setFilter({
            ...filter,
            search: "",
            district: "",
            powerstation: "",
            village: "",
            hamlet: "",
        });

        // reset search trigger & pagination
        setChangeCount(0);
        setPagination({ page: 0, limit: 100 });

        // reset flags after one render
        setTimeout(() => setClearAll(initialClearAll), 0);
    };

    const [rowsCount, setRowsCount] = useState(0);
    const [pagination, setPagination] = useState({ page: 0, limit: 100 });
    const [changeCount, setChangeCount] = useState(0);
    const [feedback, setFeedback] = useState("");

    const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM

    const getCreditPayments = () => {
        return GET("/reading/leisure-summary", {
            page: pagination.page + 1,
            limit: pagination.limit,
            meterNo: filter.search || undefined,
            district: filter.district && filter.district !== "all" ? filter.district : undefined,
            powerstation: filter.powerstation && filter.powerstation !== "all" ? filter.powerstation : undefined,
            village: filter.village && filter.village !== "all" ? filter.village : undefined,
            hamlet: filter.hamlet && filter.hamlet !== "all" ? filter.hamlet : undefined,
        });
    };

    const { data, isLoading } = useQuery(
        ["advance", pagination.page, pagination.limit, filter],
        getCreditPayments,
        {
            enabled: Boolean(changeCount),
            onSuccess(res) {
                if (res) setRowsCount(res?.data.count);
            },
        }
    );

    const createSummary = (cols: string[]) => {
        const summaryRow = ["Total"];
        if (!data?.data?.rows || !Array.isArray(data.data.rows))
            return summaryRow;

        let startBalance = 0;
        let balancedUsed = 0;
        let balanceRemaining = 0;

        for (const row of data?.data.rows) {
            startBalance += row.startBalance;
            balancedUsed += row.usedBalance;
            balanceRemaining += row.remainingBalance;
        }

        for (const col of cols) {
            switch (col) {
                case "startBalance":
                    summaryRow.push(currencyFormatter.format(startBalance));
                    break;
                case "balancedUsed":
                    summaryRow.push(
                        currencyFormatter.format(balancedUsed)
                    );
                    break;

                case "balanceRemaining":
                    summaryRow.push(
                        currencyFormatter.format(balanceRemaining)
                    );
                    break;
                default:
                    summaryRow.push("");
            }
        }
        return summaryRow;
    };

    // When user clicks Search button
    useEffect(() => {
        if (changeCount > 0) {
            setFilter((prev: any) => ({
                ...prev,
                meterNo: prev.search,
            }));
        }
    }, [changeCount]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Card elevation={0} sx={{ p: 2, mb: "1rem" }}>
                <Grid container spacing={1.5}>
                    <Grid item xs={12} md="auto">
                        <Typography color="GrayText" variant="h5">
                            Credit Payments ledger Summary
                        </Typography>
                    </Grid>
                </Grid>

                <Grid item container xs={12} spacing={1.5} pt={2}>
                    <MultiPermissionAuthorize ops={["READ DISTRICT"]}>
                        <Grid item xs={12} md={4} lg={3}>
                            <UpdatedSearchableInput
                                label="District"
                                api="/region/district"
                                filter={filter}
                                setFilter={setFilter}
                                clearAll={clearAll.district}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ POWERSTATION"]}>
                        <Grid item xs={12} md={4} lg={3}>
                            <UpdatedSearchableInput
                                label="Powerstation"
                                api="/powerstation"
                                filter={filter}
                                setFilter={setFilter}
                                dep={filter.district}
                                params={{ district: filter.district }}
                                clearAll={clearAll.powerstation}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ VILLAGE"]}>
                        <Grid item xs={12} md={6} lg={3}>
                            <UpdatedSearchableInput
                                api="/region/village"
                                label="Village"
                                filter={filter}
                                setFilter={setFilter}
                                dep={[
                                    filter.district,
                                    filter.powerstation,
                                ]}
                                params={{
                                    district: filter.district,
                                    powerstation:
                                        filter.powerstation,
                                }}
                                clearAll={clearAll.village}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ HAMLET"]}>
                        <Grid item xs={12} md={6} lg={3}>
                            <UpdatedSearchableInput
                                api="/region/hamlet"
                                label="Hamlet"
                                filter={filter}
                                setFilter={setFilter}
                                dep={[
                                    filter.village,
                                    filter.powerstation,
                                ]}
                                params={{
                                    village: filter.village,
                                    powerstation:
                                        filter.powerstation,
                                }}
                                clearAll={clearAll.hamlet}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <Grid item xs={12} md={4} lg={3}>
                        <SearchBar
                            filter={filter}
                            setFilter={setFilter}
                            clear={clearAll.search}
                        />
                    </Grid>

                    <Grid item xs={12} md={4} lg={3}>
                        <Button
                            color="secondary"
                            variant="outlined"
                            startIcon={<FilterAltOutlined />}
                            onClick={() => setChangeCount((prev) => prev + 1)}
                            sx={{ minWidth: "20ch", height: "100%" }}
                            fullWidth
                        >
                            Search
                        </Button>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Button
                            color="error"
                            variant="outlined"
                            startIcon={<FilterAltOffOutlined />}
                            onClick={handleClearAll}
                            sx={{ minWidth: "20ch", height: "100%" }}
                            fullWidth
                        >
                            Clear All
                        </Button>
                    </Grid>
                </Grid>

                {!filter.search && !filter.district && !filter.powerstation ? (
                    <Alert severity="info" style={{ marginTop: "1rem" }}>
                        Must provide Meter No. or select a District/Powerstation in order to view credit payment leisure.
                    </Alert>
                ) : (
                    <Box sx={{ height: "70%" }}>
                        <BaseTable
                            headers={{
                                meterNo: "Meter No.",
                                name: "Name",
                                startBalance: "Start Balance",
                                balancedUsed: "Balance Used",
                                balanceRemaining: "Balance Remaining",
                                districtName: "District",
                                villageName: "Village",
                                hamletName: "Hamlet",
                                powerstationName: "Power Station",
                            }}
                            data={
                                data &&
                                data?.data.rows.map((row: any) => ({
                                    ...row,
                                    meterNo: row.meterNo,
                                    name: row?.customerName,
                                    startBalance: row.startBalance,
                                    balancedUsed: row.usedBalance,
                                    balanceRemaining: row.remainingBalance,
                                    districtName: row.districtName,
                                    villageName: row.villageName,
                                    hamletName: row.hamletName,
                                    powerstationName: row.powerstationName,
                                }))
                            }
                            delEndPoint=""
                            defaultSelectedHeaders={["id"]}
                            feedback={feedback}
                            setFeedback={setFeedback}
                            rowsCount={rowsCount}
                            pagination={pagination}
                            setPagination={setPagination}
                            isLoading={isLoading}
                            load={Boolean(changeCount)}
                            permissions={{
                                edit: ["NO EDIT"],
                                delete: ["NO DELETE"],
                            }}
                            queryKey={[
                                "advance",
                                pagination.page,
                                pagination.limit,
                                filter,
                            ]}
                            reportName="CreditPayment__List"
                            summaryRow={createSummary}
                        />
                    </Box>
                )}
            </Card>
        </Box>
    );
};

export default CreditPaymentLeasure;
